import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import { db } from '../../firebase/firebaseConfig';
import { ref, onValue, get } from 'firebase/database';

function AdminDashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalProfiles: 0,
    totalApiRequests: 0,
    activeToday: 0,
    newToday: 0
  });

  const [growthData, setGrowthData] = useState([]);
  const [platformData, setPlatformData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1. Listen for User & Profile Growth
    const usersRef = ref(db, 'users');
    const unsubscribeUsers = onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const users = snapshot.val();
        const userList = Object.values(users);
        const userCount = userList.length;

        // Process growth data (last 7 days)
        const countsByDay = {};
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        
        // Initialize last 7 days
        for(let i=6; i>=0; i--) {
          const d = new Date();
          d.setDate(d.getDate() - i);
          countsByDay[days[d.getDay()]] = 0;
        }

        userList.forEach(u => {
          if (u.createdAt) {
            const date = new Date(u.createdAt);
            const dayName = days[date.getDay()];
            if (countsByDay[dayName] !== undefined) {
              countsByDay[dayName]++;
            }
          }
        });

        const growth = Object.keys(countsByDay).map(day => ({
          name: day,
          users: countsByDay[day]
        }));
        
        setGrowthData(growth);

        // Calculate today's stats
        const todayStart = new Date().setHours(0,0,0,0);
        const newToday = userList.filter(u => u.createdAt >= todayStart).length;

        setMetrics(prev => ({
          ...prev,
          totalUsers: userCount,
          newToday: newToday,
          activeToday: Math.floor(userCount * 0.4) // Still mock active for now as we don't track sessions 1:1 yet
        }));
      }
    });

    // 2. Synchronize Profiles Metric (from Users collection)
    const unsubscribeProfiles = onValue(usersRef, (snapshot) => {
      if (snapshot.exists()) {
        const usersList = Object.values(snapshot.val());
        const totalProfiles = usersList.filter(u => u.profileId).length;
        setMetrics(prev => ({
          ...prev,
          totalProfiles: totalProfiles
        }));
      }
    });

    // 3. Listen for API Stats
    const statsRef = ref(db, 'stats/api_usage');
    const unsubscribeStats = onValue(statsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setMetrics(prev => ({
          ...prev,
          totalApiRequests: data.total || 0
        }));

        const platformArr = [
          { name: 'GitHub', requests: data.GitHub || 0, fill: '#ffffff' },
          { name: 'LeetCode', requests: data.LeetCode || 0, fill: '#F59E0B' }, // Warning color
          { name: 'Codeforces', requests: data.Codeforces || 0, fill: '#2563EB' }, // Info color
          { name: 'GFG', requests: data.GeeksforGeeks || 0, fill: '#16A34A' } // Success color
        ];
        setPlatformData(platformArr);
      }
      setLoading(false);
    });

    return () => {
      unsubscribeUsers();
      unsubscribeProfiles();
      unsubscribeStats();
    };
  }, []);

  if (loading) return <div style={{color: '#00ffae', padding: '100px', textAlign: 'center'}}>Syncing with Neural Core...</div>;

  return (
    <div className="animate-up">
      <div className="admin-page-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
        <div>
          <h1>Platform Overview</h1>
          <p>Real-time analytics and system status for DevTracker.</p>
        </div>
        <div className="security-version-badge" style={{background: 'rgba(0, 255, 174, 0.05)', border: '1px solid rgba(0, 255, 174, 0.1)', color: 'var(--brand-accent)', padding: '8px 16px', borderRadius: '50px', fontSize: '0.75rem', fontWeight: 'bold', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '8px'}}>
           <i className="fa-solid fa-shield-halved"></i>
           SEC-V2.7.0.0-SINGULARITY
        </div>
      </div>

      {/* METRIC CARDS */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '40px'}}>
        
        <div className="admin-card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px'}}>
            <i className="fa-solid fa-users" style={{color: '#00ffae', fontSize: '1.5rem', background: 'rgba(0,255,174,0.1)', padding: '10px', borderRadius: '8px'}}></i>
            <span style={{color: '#00ffae', fontSize: '0.85rem', fontWeight: 'bold'}}>LIVE</span>
          </div>
          <p style={{color: '#8b949e', fontSize: '0.9rem', marginBottom: '5px', textTransform: 'uppercase'}}>Total Users</p>
          <h2 style={{fontSize: '2rem', color: '#fff', margin: 0}}>{metrics.totalUsers}</h2>
        </div>

        <div className="admin-card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px'}}>
            <i className="fa-solid fa-id-card-clip" style={{color: '#a5b4fc', fontSize: '1.5rem', background: 'rgba(165,180,252,0.1)', padding: '10px', borderRadius: '8px'}}></i>
            <span style={{color: '#a5b4fc', fontSize: '0.85rem', fontWeight: 'bold'}}>LIVE</span>
          </div>
          <p style={{color: '#8b949e', fontSize: '0.9rem', marginBottom: '5px', textTransform: 'uppercase'}}>Profiles</p>
          <h2 style={{fontSize: '2rem', color: '#fff', margin: 0}}>{metrics.totalProfiles}</h2>
        </div>

        <div className="admin-card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px'}}>
            <i className="fa-solid fa-network-wired" style={{color: '#DC2626', fontSize: '1.5rem', background: 'rgba(220, 38, 38, 0.1)', padding: '10px', borderRadius: '8px'}}></i>
            <span style={{color: '#DC2626', fontSize: '0.85rem', fontWeight: 'bold'}}>LIVE</span>
          </div>
          <p style={{color: '#8b949e', fontSize: '0.9rem', marginBottom: '5px', textTransform: 'uppercase'}}>API Requests</p>
          <h2 style={{fontSize: '2rem', color: '#fff', margin: 0}}>{metrics.totalApiRequests.toLocaleString()}</h2>
        </div>

        <div className="admin-card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px'}}>
            <i className="fa-solid fa-bolt" style={{color: '#F59E0B', fontSize: '1.5rem', background: 'rgba(245, 158, 11, 0.1)', padding: '10px', borderRadius: '8px'}}></i>
            <span style={{color: '#F59E0B', fontSize: '0.85rem', fontWeight: 'bold'}}>NEW</span>
          </div>
          <p style={{color: '#8b949e', fontSize: '0.9rem', marginBottom: '5px', textTransform: 'uppercase'}}>Registrations Today</p>
          <h2 style={{fontSize: '2rem', color: '#fff', margin: 0}}>{metrics.newToday}</h2>
        </div>
      </div>

      {/* CHARTS GRID */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px'}}>
        
        {/* User Growth Line Chart */}
        <div className="admin-card">
          <h3 style={{color: '#fff', marginBottom: '25px', fontSize: '1.1rem'}}>7-Day User Growth</h3>
          <div style={{width: '100%', height: '300px'}}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <AreaChart data={growthData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00ffae" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#00ffae" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="name" stroke="#8b949e" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#8b949e" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip 
                  contentStyle={{background: '#161b22', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px'}}
                  itemStyle={{color: '#00ffae'}}
                />
                <Area type="monotone" dataKey="users" stroke="#00ffae" strokeWidth={3} fillOpacity={1} fill="url(#colorUsers)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* API Platform Usage Bar Chart */}
        <div className="admin-card">
          <h3 style={{color: '#fff', marginBottom: '25px', fontSize: '1.1rem'}}>API Traffic by Platform</h3>
          <div style={{width: '100%', height: '300px'}}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={platformData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="name" stroke="#8b949e" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#8b949e" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip 
                  cursor={{fill: 'rgba(255,255,255,0.05)'}}
                  contentStyle={{background: '#161b22', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px'}}
                />
                <Bar dataKey="requests" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* SYSTEM STATUS MONITOR */}
      <h3 style={{color: '#fff', margin: '40px 0 20px', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '10px'}}>
        <i className="fa-solid fa-microchip" style={{color: '#00ffae'}}></i> System Status Monitor
      </h3>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', marginBottom: '40px'}}>
        
        <div className="admin-card" style={{display: 'flex', alignItems: 'center', gap: '20px', padding: '20px'}}>
          <div className="status-orb pulse" style={{width: '12px', height: '12px', borderRadius: '50%', background: '#00ffae', boxShadow: '0 0 10px #00ffae'}}></div>
          <div style={{flex: 1}}>
            <h4 style={{color: '#fff', margin: 0, fontSize: '0.95rem'}}>Origin Core (Primary DB)</h4>
            <p style={{color: '#8b949e', margin: '4px 0 0', fontSize: '0.8rem'}}>Latence: 14ms | Status: OPTIMAL</p>
          </div>
          <i className="fa-solid fa-database" style={{color: 'rgba(255,255,255,0.1)', fontSize: '1.5rem'}}></i>
        </div>

        <div className="admin-card" style={{display: 'flex', alignItems: 'center', gap: '20px', padding: '20px'}}>
          <div className="status-orb" style={{width: '12px', height: '12px', borderRadius: '50%', background: '#00ffae', boxShadow: '0 0 10px #00ffae'}}></div>
          <div style={{flex: 1}}>
            <h4 style={{color: '#fff', margin: 0, fontSize: '0.95rem'}}>Sync Hub (API Ingress)</h4>
            <p style={{color: '#8b949e', margin: '4px 0 0', fontSize: '0.8rem'}}>Load: 12% | Status: ACTIVE</p>
          </div>
          <i className="fa-solid fa-cloud-arrow-down" style={{color: 'rgba(255,255,255,0.1)', fontSize: '1.5rem'}}></i>
        </div>

        <div className="admin-card" style={{display: 'flex', alignItems: 'center', gap: '20px', padding: '20px'}}>
          <div className="status-orb pulse" style={{width: '12px', height: '12px', borderRadius: '50%', background: '#F59E0B', boxShadow: '0 0 10px #F59E0B'}}></div>
          <div style={{flex: 1}}>
            <h4 style={{color: '#fff', margin: 0, fontSize: '0.95rem'}}>Neural DNA Engine</h4>
            <p style={{color: '#8b949e', margin: '4px 0 0', fontSize: '0.8rem'}}>Queued: 4 | Status: THROTTLED</p>
          </div>
          <i className="fa-solid fa-dna" style={{color: 'rgba(255,255,255,0.1)', fontSize: '1.5rem'}}></i>
        </div>

        <div className="admin-card" style={{display: 'flex', alignItems: 'center', gap: '20px', padding: '20px'}}>
          <div className="status-orb" style={{width: '12px', height: '12px', borderRadius: '50%', background: '#00ffae', boxShadow: '0 0 10px #00ffae'}}></div>
          <div style={{flex: 1}}>
            <h4 style={{color: '#fff', margin: 0, fontSize: '0.95rem'}}>Security Guard (Auth)</h4>
            <p style={{color: '#8b949e', margin: '4px 0 0', fontSize: '0.8rem'}}>Firewall: ACTIVE | Status: SECURE</p>
          </div>
          <i className="fa-solid fa-user-shield" style={{color: 'rgba(255,255,255,0.1)', fontSize: '1.5rem'}}></i>
        </div>

      </div>

      {/* SYSTEM SITEMAP QUICK-LINK */}
      <div className="admin-card" style={{marginTop: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(90deg, var(--surface-2) 0%, rgba(0, 255, 174, 0.05) 100%)', border: '1px solid rgba(0, 255, 174, 0.1)'}}>
        <div style={{display: 'flex', alignItems: 'center', gap: '20px'}}>
             <div style={{width: '50px', height: '50px', background: 'rgba(0, 255, 174, 0.1)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00ffae'}}>
               <i className="fa-solid fa-map-location-dot" style={{fontSize: '1.5rem'}}></i>
             </div>
             <div>
               <h3 style={{color: '#fff', margin: 0, fontSize: '1.1rem'}}>System Sitemap & Route Inventory</h3>
               <p style={{color: '#8b949e', margin: '5px 0 0', fontSize: '0.85rem'}}>View the complete hierarchical map of all platform endpoints, from Public to Origin Core.</p>
             </div>
        </div>
        <button className="btn-resolve" onClick={() => navigate('/admin/sitemap')} style={{margin: 0, padding: '10px 24px', background: 'transparent', border: '1px solid #00ffae', color: '#00ffae'}}>
          <i className="fa-solid fa-compass"></i> Open Sitemap
        </button>
      </div>

    </div>
  );
}

export default AdminDashboard;
